import { useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import { EditorBaseProps } from 'modules/workflowNodeEditors/types';
import { PREVIEW_YES_NO_SPLIT_MODAL } from 'modules/workflows/constants';
import { openModal } from 'modules/workflows/ducks';
import { FieldMatchNodeData, SearchCombo } from 'modules/workflows/types';
import { walkSearchQueries } from 'modules/workflows/utils';
import { BUTTON_SIZES, BUTTON_VARIANTS, Button } from 'ui/button';
import { YES_NO_PREVIEW_BUTTON_TEST_ID } from '../YesNoSplitEditor.constants';
import { isQueryIncomplete } from './TestSplitResultsButton.utils';

export interface TestSplitResultsButtonProps
  extends Pick<EditorBaseProps<FieldMatchNodeData>, 'node' | 'nodeId' | 'workflowData'> {
  searchQuery?: SearchCombo;
}

/**
 * Button that launches a modal to test the split results for a node
 */
const TestSplitResultsButton = ({
  node,
  nodeId,
  searchQuery,
  workflowData,
}: TestSplitResultsButtonProps) => {
  const dispatch = useDispatch();

  const previewIsDisabled = useMemo(() => {
    let isDisabled = false;

    walkSearchQueries(searchQuery?.searchQueries || [], {
      callback: ({ query, stop }) => {
        if (isQueryIncomplete(query)) {
          isDisabled = true;
          stop();
        }
      },
    });

    return isDisabled;
  }, [searchQuery]);

  const openPreviewModal = useCallback(() => {
    dispatch(
      openModal(PREVIEW_YES_NO_SPLIT_MODAL, {
        node,
        nodeId,
        journeyId: workflowData?.id,
        projectId: workflowData?.projectId,
        searchQuery,
        workflowType: workflowData?.workflowType,
      }),
    );
  }, [nodeId, node, workflowData, searchQuery, dispatch]);

  return (
    <Button
      data-test={YES_NO_PREVIEW_BUTTON_TEST_ID}
      isDisabled={previewIsDisabled}
      onClick={openPreviewModal}
      size={BUTTON_SIZES.LARGE}
      variant={BUTTON_VARIANTS.SECONDARY}
    >
      Preview results
    </Button>
  );
};

export default TestSplitResultsButton;
